import {
  AlertTriangle,
  BookOpen,
  ChevronRight,
  ExternalLink,
  Library,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useLocation } from '#router'
import { activatableRowProps } from '@/components/admin/shared/activatable-row'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  REVEAL_ON_HOVER,
  REVEAL_ON_HOVER_DECORATIVE,
} from '@/components/ui/reveal-on-hover'
import { cn } from '@/lib/utils'
import { AddSourceModal } from './add-source-modal'
import { useSources } from './hooks/use-sources'
import type { Source } from './types'
import { SOURCE_CATEGORIES } from './types'

function getCategoryLabel(category: string): string {
  return SOURCE_CATEGORIES.find((c) => c.value === category)?.label || category
}

interface SourceRowProps {
  source: Source
  onClick: (source: Source) => void
  onDelete: (e: React.MouseEvent, source: Source) => void
}

function SourceRow({ source, onClick, onDelete }: SourceRowProps) {
  return (
    <div
      {...activatableRowProps({
        onActivate: () => onClick(source),
        label: `Edit ${source.name}`,
      })}
      className="hover:bg-muted/50 group flex cursor-pointer items-center gap-4 p-4 transition-colors"
    >
      {/* Icon */}
      <div className="bg-muted flex h-10 w-10 items-center justify-center rounded text-lg">
        <AuthoredIcon icon={source.icon} fallback={Library} />
      </div>

      {/* Content */}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h3 className="text-foreground font-medium">{source.name}</h3>
        </div>
        <div className="text-muted-foreground mt-0.5 flex items-center gap-2 text-sm">
          <span>{source.slug}</span>
          {source.url && (
            <>
              <span>•</span>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="hover:text-foreground flex items-center gap-1"
              >
                <ExternalLink size={12} />
                Link
              </a>
            </>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={(e) => onDelete(e, source)}
          className={cn(
            REVEAL_ON_HOVER,
            'text-muted-foreground hover:text-destructive rounded p-2 transition-all',
          )}
          title="Delete source"
        >
          <Trash2 size={16} />
        </button>
        <ChevronRight
          size={18}
          className={cn(REVEAL_ON_HOVER_DECORATIVE, 'text-muted-foreground')}
        />
      </div>
    </div>
  )
}

export function SourcesList() {
  const { navigate } = useLocation()
  const { sources, loading, error, refresh, addSource, deleteSource } =
    useSources()
  const [showAddModal, setShowAddModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [sourceToDelete, setSourceToDelete] = useState<Source | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  // Filter sources based on search query
  const filteredSources = useMemo(() => {
    if (!searchQuery.trim()) return sources
    const query = searchQuery.toLowerCase()
    return sources.filter(
      (source) =>
        source.name.toLowerCase().includes(query) ||
        source.slug.toLowerCase().includes(query) ||
        getCategoryLabel(source.category).toLowerCase().includes(query),
    )
  }, [sources, searchQuery])

  const prepResources = filteredSources

  const handleSourceClick = (source: Source) => {
    navigate(`/admin/sources/${source.id}`)
  }

  const handleDeleteClick = (e: React.MouseEvent, source: Source) => {
    e.stopPropagation()
    setSourceToDelete(source)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!sourceToDelete) return

    setIsDeleting(true)
    try {
      await deleteSource(sourceToDelete.id)
      toast.success(`"${sourceToDelete.name}" deleted successfully`)
      setShowDeleteConfirm(false)
      setSourceToDelete(null)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to delete source',
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const handleAddSource = async (data: Parameters<typeof addSource>[0]) => {
    const newSource = await addSource(data)
    toast.success(`"${newSource.name}" created successfully`)
    return newSource
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading sources...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="mx-auto max-w-6xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 p-2">
                <Library size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-foreground text-xl font-bold">Sources</h1>
                <p className="text-muted-foreground text-sm">
                  {sources.length} total sources
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={() => setShowAddModal(true)}>
                <Plus size={18} className="mr-2" />
                Add Source
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-6xl px-6 py-6">
        {error ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Library size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              Unable to load sources
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={refresh} variant="outline">
              <RefreshCw size={18} className="mr-2" />
              Retry
            </Button>
          </div>
        ) : sources.length === 0 ? (
          <div className="py-16 text-center">
            <div className="bg-muted mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
              <Library size={32} className="text-muted-foreground" />
            </div>
            <h3 className="text-foreground mb-2 text-lg font-medium">
              No sources yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Add your first source to get started.
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus size={18} className="mr-2" />
              Add Source
            </Button>
          </div>
        ) : (
          <div>
            {/* Search header */}
            <div className="mb-6 flex items-center justify-between gap-4">
              <div className="relative w-64">
                <Search
                  size={16}
                  className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search sources..."
                  className="pl-9"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-muted-foreground text-sm">
                  Showing {filteredSources.length} of {sources.length} sources
                </p>
              )}
            </div>

            {/* Sources list */}
            {filteredSources.length === 0 ? (
              <div className="py-12 text-center">
                <Search
                  size={32}
                  className="text-muted-foreground mx-auto mb-3"
                />
                <p className="text-muted-foreground">
                  No sources found matching "{searchQuery}"
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Prep Resources Section */}
                {prepResources.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <BookOpen
                        size={16}
                        className="text-muted-foreground/60"
                      />
                      <h2 className="text-muted-foreground/70 text-sm font-medium uppercase tracking-wide">
                        Prep Resources
                      </h2>
                      <span className="bg-muted-foreground/20 text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium">
                        {prepResources.length}
                      </span>
                    </div>
                    <div className="bg-card border-border divide-border divide-y overflow-hidden rounded-lg border">
                      {prepResources.map((source) => (
                        <SourceRow
                          key={source.id}
                          source={source}
                          onClick={handleSourceClick}
                          onDelete={handleDeleteClick}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Source Modal */}
      <AddSourceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddSource}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && sourceToDelete && (
        <div className="bg-background/80 fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-destructive/10 flex h-10 w-10 items-center justify-center rounded-full">
                <AlertTriangle size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-foreground text-lg font-semibold">
                  Delete Source
                </h2>
                <p className="text-muted-foreground text-sm">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-foreground mb-6">
              Are you sure you want to delete{' '}
              <strong>{sourceToDelete.name}</strong>? This will remove the
              source but won't affect any associated problems.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setSourceToDelete(null)
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Source'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
